import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  FormHelperText,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Divider,
  RadioGroup,
  Radio,
  Paper,
} from '@mui/material';
import {
  Business as BusinessIcon,
  VideoLibrary as VideoIcon,
  Schedule as ScheduleIcon,
  Publish as PublishIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { projectService } from '../services/api';
import { useSubscription } from '../contexts/SubscriptionContext';

const steps = [
  {
    label: 'Project Details',
    icon: <BusinessIcon />,
    description: 'Basic information about your project',
  },
  {
    label: 'Content Topics',
    icon: <VideoIcon />,
    description: 'What kind of content will you create?',
  },
  {
    label: 'Visual Settings',
    icon: <VideoIcon />,
    description: 'Choose how visuals are generated',
  },
  {
    label: 'Publishing Platforms',
    icon: <PublishIcon />,
    description: 'Where do you want to publish your reels?',
  },
  {
    label: 'Schedule Settings',
    icon: <ScheduleIcon />,
    description: 'When should we publish your content?',
  },
];

const platforms = [
  { id: 'instagram', name: 'Instagram', icon: <InstagramIcon />, color: '#E4405F' },
  { id: 'facebook', name: 'Facebook', icon: <FacebookIcon />, color: '#1877F2' },
  { id: 'youtube', name: 'YouTube Shorts', icon: <YouTubeIcon />, color: '#FF0000' },
  { id: 'tiktok', name: 'TikTok', icon: <VideoIcon />, color: '#000000' },
  { id: 'twitter', name: 'X (Twitter)', icon: <TwitterIcon />, color: '#1DA1F2' },
];

const contentTypes = [
  'Educational Facts',
  'Life Hacks & Tips',
  'Motivational Quotes',
  'Tech News & Updates',
  'Science & Discovery',
  'History & Culture',
  'Health & Wellness',
  'Business & Finance',
  'Entertainment News',
  'Travel & Adventure',
  'Food & Recipes',
  'DIY & Crafts',
];

const ProjectOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { subscription } = useSubscription();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data
  const [projectData, setProjectData] = useState({
    // Step 1: Project Details
    name: '',
    description: '',
    brand_voice: 'professional',
    target_audience: '',
    
    // Step 2: Content Topics
    content_types: [] as string[],
    custom_topics: [] as string[],
    content_length: '30',
    
    // Step 3: Visual Settings
    visual_source: 'stock_images',
    stock_provider: 'pexels',
    pexels_api_key: '',
    fireworks_api_key: '',
    segmind_api_key: '',
    visual_config: {
      image_count: 5,
      ai_video_count: 3,
      ai_provider: 'fireworks',
      video_count: 5,
      ai_style: '',
      ai_image_count: 5,
      ai_fallback_enabled: false,
    } as any,
    
    // Step 4: Publishing Platforms
    platforms: {
      instagram: false,
      facebook: false,
      youtube: false,
      tiktok: false,
      twitter: false,
    },
    
    // Step 5: Schedule Settings
    publishing_schedule: {
      monday: { enabled: true, times: ['09:00', '18:00'] },
      tuesday: { enabled: true, times: ['09:00', '18:00'] },
      wednesday: { enabled: true, times: ['09:00', '18:00'] },
      thursday: { enabled: true, times: ['09:00', '18:00'] },
      friday: { enabled: true, times: ['09:00', '18:00'] },
      saturday: { enabled: false, times: ['12:00'] },
      sunday: { enabled: false, times: ['12:00'] },
    },
    timezone: 'America/New_York',
    auto_publish: false,
  });

  const [customTopic, setCustomTopic] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 0:
        if (!projectData.name.trim()) {
          newErrors.name = 'Project name is required';
        }
        if (!projectData.description.trim()) {
          newErrors.description = 'Description is required';
        }
        if (!projectData.target_audience.trim()) {
          newErrors.target_audience = 'Target audience is required';
        }
        break;

      case 1:
        if (projectData.content_types.length === 0 && projectData.custom_topics.length === 0) {
          newErrors.content_types = 'Select at least one content type or add custom topics';
        }
        break;

      case 2:
        if (projectData.visual_source === 'stock_images' || projectData.visual_source === 'stock_videos') {
          // Pexels API key is required for stock content
          if (!projectData.pexels_api_key) {
            newErrors.api_keys = 'Pexels API key is required for stock content';
          }
        }
        if (projectData.visual_source === 'ai_images' && !projectData.fireworks_api_key) {
          newErrors.fireworks_api_key = 'Fireworks API key is required for AI images';
        }
        if ((projectData.visual_source === 'ai_videos_svd' || projectData.visual_source === 'ai_videos_t2v') && !projectData.segmind_api_key) {
          newErrors.segmind_api_key = 'Segmind API key is required for AI video generation';
        }
        break;

      case 3:
        const hasSelectedPlatform = Object.values(projectData.platforms).some(v => v);
        if (!hasSelectedPlatform) {
          newErrors.platforms = 'Select at least one publishing platform';
        }
        break;

      case 4:
        const hasEnabledDay = Object.values(projectData.publishing_schedule).some(day => day.enabled);
        if (!hasEnabledDay) {
          newErrors.schedule = 'Enable at least one day for publishing';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleContentTypeToggle = (type: string) => {
    setProjectData(prev => ({
      ...prev,
      content_types: prev.content_types.includes(type)
        ? prev.content_types.filter(t => t !== type)
        : [...prev.content_types, type]
    }));
  };

  const handleAddCustomTopic = () => {
    if (customTopic.trim()) {
      setProjectData(prev => ({
        ...prev,
        custom_topics: [...prev.custom_topics, customTopic.trim()]
      }));
      setCustomTopic('');
    }
  };

  const handleRemoveCustomTopic = (topic: string) => {
    setProjectData(prev => ({
      ...prev,
      custom_topics: prev.custom_topics.filter(t => t !== topic)
    }));
  };

  const handlePlatformToggle = (platform: string) => {
    setProjectData(prev => ({
      ...prev,
      platforms: {
        ...prev.platforms,
        [platform]: !prev.platforms[platform as keyof typeof prev.platforms]
      }
    }));
  };

  const handleDayToggle = (day: string) => {
    setProjectData(prev => ({
      ...prev,
      publishing_schedule: {
        ...prev.publishing_schedule,
        [day]: {
          ...prev.publishing_schedule[day as keyof typeof prev.publishing_schedule],
          enabled: !prev.publishing_schedule[day as keyof typeof prev.publishing_schedule].enabled
        }
      }
    }));
  };

  const handleTimeChange = (day: string, index: number, value: string) => {
    setProjectData(prev => ({
      ...prev,
      publishing_schedule: {
        ...prev.publishing_schedule,
        [day]: {
          ...prev.publishing_schedule[day as keyof typeof prev.publishing_schedule],
          times: prev.publishing_schedule[day as keyof typeof prev.publishing_schedule].times.map((t, i) =>
            i === index ? value : t
          )
        }
      }
    }));
  };

  const handleAddTime = (day: string) => {
    setProjectData(prev => ({
      ...prev,
      publishing_schedule: {
        ...prev.publishing_schedule,
        [day]: {
          ...prev.publishing_schedule[day as keyof typeof prev.publishing_schedule],
          times: [...prev.publishing_schedule[day as keyof typeof prev.publishing_schedule].times, '12:00']
        }
      }
    }));
  };

  const handleRemoveTime = (day: string, index: number) => {
    setProjectData(prev => ({
      ...prev,
      publishing_schedule: {
        ...prev.publishing_schedule,
        [day]: {
          ...prev.publishing_schedule[day as keyof typeof prev.publishing_schedule],
          times: prev.publishing_schedule[day as keyof typeof prev.publishing_schedule].times.filter((_, i) => i !== index)
        }
      }
    }));
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return;

    setIsSubmitting(true);
    try {
      const formData = {
        name: projectData.name,
        description: projectData.description,
        settings: {
          brand_voice: projectData.brand_voice,
          target_audience: projectData.target_audience,
          content_types: [...projectData.content_types, ...projectData.custom_topics],
          content_length: parseInt(projectData.content_length),
          visual_source: projectData.visual_source,
          visual_config: JSON.stringify({
            stock_provider: projectData.stock_provider,
            ...projectData.visual_config
          }),
          pexels_api_key: projectData.pexels_api_key,
          fireworks_api_key: projectData.fireworks_api_key,
          platforms: Object.entries(projectData.platforms)
            .filter(([_, enabled]) => enabled)
            .map(([platform]) => platform),
          publishing_schedule: projectData.publishing_schedule,
          timezone: projectData.timezone,
          auto_publish: projectData.auto_publish,
        }
      };

      const response = await projectService.create(formData);
      enqueueSnackbar('Project created successfully!', { variant: 'success' });
      navigate(`/projects/${response.data.id}`);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to create project', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Project Name"
                  value={projectData.name}
                  onChange={(e) => setProjectData(prev => ({ ...prev, name: e.target.value }))}
                  error={!!errors.name}
                  helperText={errors.name}
                  placeholder="e.g., My Fitness Journey"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={projectData.description}
                  onChange={(e) => setProjectData(prev => ({ ...prev, description: e.target.value }))}
                  error={!!errors.description}
                  helperText={errors.description}
                  placeholder="Describe what this project is about..."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Brand Voice</InputLabel>
                  <Select
                    value={projectData.brand_voice}
                    onChange={(e) => setProjectData(prev => ({ ...prev, brand_voice: e.target.value }))}
                    label="Brand Voice"
                  >
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="casual">Casual</MenuItem>
                    <MenuItem value="humorous">Humorous</MenuItem>
                    <MenuItem value="educational">Educational</MenuItem>
                    <MenuItem value="inspirational">Inspirational</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Target Audience"
                  value={projectData.target_audience}
                  onChange={(e) => setProjectData(prev => ({ ...prev, target_audience: e.target.value }))}
                  error={!!errors.target_audience}
                  helperText={errors.target_audience}
                  placeholder="e.g., Young professionals, Fitness enthusiasts"
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Select Content Types
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {contentTypes.map((type) => (
                <Chip
                  key={type}
                  label={type}
                  onClick={() => handleContentTypeToggle(type)}
                  color={projectData.content_types.includes(type) ? 'primary' : 'default'}
                  variant={projectData.content_types.includes(type) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Custom Topics
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Add custom topic"
                value={customTopic}
                onChange={(e) => setCustomTopic(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCustomTopic()}
              />
              <Button
                variant="contained"
                onClick={handleAddCustomTopic}
                startIcon={<AddIcon />}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {projectData.custom_topics.map((topic) => (
                <Chip
                  key={topic}
                  label={topic}
                  onDelete={() => handleRemoveCustomTopic(topic)}
                  color="secondary"
                />
              ))}
            </Box>

            {errors.content_types && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.content_types}
              </Alert>
            )}

            <Box sx={{ mt: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Content Length</InputLabel>
                <Select
                  value={projectData.content_length}
                  onChange={(e) => setProjectData(prev => ({ ...prev, content_length: e.target.value }))}
                  label="Content Length"
                >
                  <MenuItem value="15">15 seconds</MenuItem>
                  <MenuItem value="30">30 seconds</MenuItem>
                  <MenuItem value="60">60 seconds</MenuItem>
                  <MenuItem value="90">90 seconds</MenuItem>
                </Select>
                <FormHelperText>Default duration for your reels</FormHelperText>
              </FormControl>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Visual Content Settings
            </Typography>
            
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>
                Choose how visuals are generated for your reels:
              </Typography>
              <RadioGroup
                value={projectData.visual_source}
                onChange={(e) => setProjectData(prev => ({ ...prev, visual_source: e.target.value }))}
              >
                <FormControlLabel 
                  value="stock_images" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="subtitle1">Stock Images</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use high-quality stock images with Ken Burns effect
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="stock_videos" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="subtitle1">Stock Videos</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use dynamic stock video footage
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="ai_images" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="subtitle1">AI Generated Images</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Generate unique images using AI (Fireworks/Segmind)
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="ai_videos_svd" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="subtitle1">AI Generated Videos (SVD Pipeline)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Generate images first, then animate them into videos (~$0.17/video)
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel 
                  value="ai_videos_t2v" 
                  control={<Radio />} 
                  label={
                    <Box>
                      <Typography variant="subtitle1">AI Generated Videos (Direct T2V)</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Generate videos directly from text prompts (~$0.40/video)
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {(projectData.visual_source === 'stock_images' || projectData.visual_source === 'stock_videos') && (
              <Paper sx={{ p: 2, mb: 3 }}>
                {subscription?.hasByoaAddon ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom>
                      Stock Provider
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Using Pexels for stock content
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle1" gutterBottom>
                      Stock Content
                    </Typography>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Stock content included with your managed plan - no API setup required!
                    </Alert>
                  </>
                )}
                
                {subscription?.hasByoaAddon && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Configure your Pexels API key to access stock content.
                    </Alert>
                    
                    <TextField
                      fullWidth
                      label="Pexels API Key"
                      value={projectData.pexels_api_key}
                      onChange={(e) => setProjectData(prev => ({ ...prev, pexels_api_key: e.target.value }))}
                      error={!!errors.api_keys && !projectData.pexels_api_key}
                      helperText="Get your API key from pexels.com/api"
                      sx={{ mb: 2 }}
                    />
                    
                    {errors.api_keys && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {errors.api_keys}
                      </Alert>
                    )}
                  </Box>
                )}
                
                <TextField
                  fullWidth
                  type="number"
                  label={projectData.visual_source === 'stock_images' ? "Images per reel" : "Videos per reel"}
                  value={projectData.visual_source === 'stock_images' ? projectData.visual_config.image_count : projectData.visual_config.video_count}
                  onChange={(e) => setProjectData(prev => ({ 
                    ...prev, 
                    visual_config: {
                      ...prev.visual_config,
                      [projectData.visual_source === 'stock_images' ? 'image_count' : 'video_count']: parseInt(e.target.value)
                    }
                  }))}
                  inputProps={{ min: 3, max: 10 }}
                  sx={{ mt: 2 }}
                />
                
                <Divider sx={{ my: 2 }} />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={projectData.visual_config.ai_fallback_enabled}
                      onChange={(e) => setProjectData(prev => ({ 
                        ...prev, 
                        visual_config: {
                          ...prev.visual_config,
                          ai_fallback_enabled: e.target.checked
                        }
                      }))}
                    />
                  }
                  label="Enable AI Image Fallback"
                />
                <FormHelperText>
                  When enabled, AI-generated images will be used automatically if no stock content is found on Pexels
                </FormHelperText>
                
                {projectData.visual_config.ai_fallback_enabled && !projectData.fireworks_api_key && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    AI fallback is enabled but Fireworks API key is not configured. Please add your Fireworks API key to use this feature.
                  </Alert>
                )}
              </Paper>
            )}

            {projectData.visual_source === 'ai_images' && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  AI Image Generation
                </Typography>
                <TextField
                  fullWidth
                  label="Fireworks API Key"
                  value={projectData.fireworks_api_key}
                  onChange={(e) => setProjectData(prev => ({ ...prev, fireworks_api_key: e.target.value }))}
                  error={!!errors.fireworks_api_key}
                  helperText={errors.fireworks_api_key || "Get your API key from fireworks.ai"}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="AI Style (optional)"
                  value={projectData.visual_config.ai_style}
                  onChange={(e) => setProjectData(prev => ({ 
                    ...prev, 
                    visual_config: {
                      ...prev.visual_config,
                      ai_style: e.target.value
                    }
                  }))}
                  placeholder="e.g., cinematic, photorealistic, anime style"
                  helperText="Add style modifiers to influence the generated images"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Images per reel"
                  value={projectData.visual_config.ai_image_count}
                  onChange={(e) => setProjectData(prev => ({ 
                    ...prev, 
                    visual_config: {
                      ...prev.visual_config,
                      ai_image_count: parseInt(e.target.value)
                    }
                  }))}
                  inputProps={{ min: 3, max: 10 }}
                />
              </Paper>
            )}

            {(projectData.visual_source === 'ai_videos_svd' || projectData.visual_source === 'ai_videos_t2v') && (
              <Paper sx={{ p: 2, mb: 3 }}>
                {subscription?.hasByoaAddon ? (
                  <>
                    <Typography variant="subtitle1" gutterBottom>
                      AI Video Generation - Segmind
                    </Typography>
                    <TextField
                      fullWidth
                      label="Segmind API Key"
                      value={projectData.segmind_api_key}
                      onChange={(e) => setProjectData(prev => ({ ...prev, segmind_api_key: e.target.value }))}
                      error={!!errors.segmind_api_key}
                      helperText={errors.segmind_api_key || "Get your API key from segmind.com"}
                      sx={{ mb: 2 }}
                    />
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle1" gutterBottom>
                      AI Video Generation
                    </Typography>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      AI video generation included with your managed plan - no API setup required!
                    </Alert>
                  </>
                )}
                <TextField
                  fullWidth
                  label="AI Style (optional)"
                  value={projectData.visual_config.ai_style}
                  onChange={(e) => setProjectData(prev => ({ 
                    ...prev, 
                    visual_config: {
                      ...prev.visual_config,
                      ai_style: e.target.value
                    }
                  }))}
                  placeholder="e.g., cinematic, professional, artistic"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Videos per reel"
                  value={projectData.visual_config.ai_video_count}
                  onChange={(e) => setProjectData(prev => ({ 
                    ...prev, 
                    visual_config: {
                      ...prev.visual_config,
                      ai_video_count: parseInt(e.target.value)
                    }
                  }))}
                  inputProps={{ min: 2, max: 5 }}
                  helperText={`${projectData.visual_source === 'ai_videos_svd' ? 'SVD Pipeline: ~$0.17' : 'Direct T2V: ~$0.40'} per video`}
                />
              </Paper>
            )}

            {projectData.visual_source === 'ai_images' && (
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  AI Image Provider Selection
                </Typography>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Choose your AI image provider:
                  </Typography>
                  <RadioGroup
                    value={projectData.visual_config.ai_provider || 'fireworks'}
                    onChange={(e) => setProjectData(prev => ({ 
                      ...prev, 
                      visual_config: {
                        ...prev.visual_config,
                        ai_provider: e.target.value
                      }
                    }))}
                  >
                    <FormControlLabel 
                      value="fireworks" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography variant="subtitle2">Fireworks AI (Original)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ~$0.004 per image - Predictable pricing
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel 
                      value="segmind" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography variant="subtitle2">Segmind SDXL (Cost-Optimized)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ~$0.002 per image - 75% cheaper, variable pricing
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Paper>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Select Publishing Platforms
            </Typography>
            <Grid container spacing={2}>
              {platforms.map((platform) => (
                <Grid item xs={12} sm={6} key={platform.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: 2,
                      borderColor: projectData.platforms[platform.id as keyof typeof projectData.platforms] 
                        ? platform.color 
                        : 'transparent',
                      transition: 'all 0.3s',
                      '&:hover': {
                        borderColor: platform.color,
                        opacity: 0.8,
                      },
                    }}
                    onClick={() => handlePlatformToggle(platform.id)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ color: platform.color }}>
                          {platform.icon}
                        </Box>
                        <Typography variant="h6">{platform.name}</Typography>
                        {projectData.platforms[platform.id as keyof typeof projectData.platforms] && (
                          <CheckIcon sx={{ ml: 'auto', color: 'success.main' }} />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {errors.platforms && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.platforms}
              </Alert>
            )}
          </Box>
        );

      case 4:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Publishing Schedule
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={projectData.timezone}
                onChange={(e) => setProjectData(prev => ({ ...prev, timezone: e.target.value }))}
                label="Timezone"
              >
                <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                <MenuItem value="Europe/London">London (GMT)</MenuItem>
                <MenuItem value="Europe/Paris">Paris (CET)</MenuItem>
                <MenuItem value="Asia/Tokyo">Tokyo (JST)</MenuItem>
              </Select>
            </FormControl>

            <List>
              {Object.entries(projectData.publishing_schedule).map(([day, schedule]) => (
                <ListItem key={day} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Switch
                      checked={schedule.enabled}
                      onChange={() => handleDayToggle(day)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={day.charAt(0).toUpperCase() + day.slice(1)}
                    sx={{ textTransform: 'capitalize' }}
                  />
                  <Collapse in={schedule.enabled} sx={{ width: '60%' }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {schedule.times.map((time, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            type="time"
                            size="small"
                            value={time}
                            onChange={(e) => handleTimeChange(day, index, e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                          {schedule.times.length > 1 && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveTime(day, index)}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                      <IconButton
                        size="small"
                        onClick={() => handleAddTime(day)}
                        sx={{ border: 1, borderColor: 'divider' }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Collapse>
                </ListItem>
              ))}
            </List>

            <FormControlLabel
              control={
                <Switch
                  checked={projectData.auto_publish}
                  onChange={(e) => setProjectData(prev => ({ ...prev, auto_publish: e.target.checked }))}
                />
              }
              label="Auto-publish reels when ready"
              sx={{ mt: 2 }}
            />

            {errors.schedule && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.schedule}
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          Back to Projects
        </Button>
        <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 700 }}>
          Create New Project
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Let's set up your content automation project
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: activeStep >= index ? 'primary.main' : 'action.disabledBackground',
                        color: 'white',
                      }}
                    >
                      {activeStep > index ? <CheckIcon /> : step.icon}
                    </Box>
                  )}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: activeStep === index ? 600 : 400 }}>
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Grid>

        <Grid item xs={12} md={9}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {renderStepContent(activeStep)}
                </motion.div>
              </AnimatePresence>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                >
                  Back
                </Button>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    endIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckIcon />}
                  >
                    {isSubmitting ? 'Creating Project...' : 'Create Project'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForwardIcon />}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectOnboarding;